import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import styled, { css } from "styled-components";

import ReadParameter from "./ReadParameter.graphql";
import UpsertParameter from "./UpsertParameter.graphql";
import * as UI from "../../../../../modules/ui-kit";
import Modal from "../../../components/Modal";
import ListParameterGroups from "../../ParameterGroup/ParameterGroupInventory/ListParameterGroups.graphql";

const StyledParameterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    margin-bottom: 16px;

    .label {
        color: #42526e;
        font-size: 0.9rem;
        font-weight: 600;
    }
`;

const StyledModalContent = styled.div`
    display: flex;
    flex-direction: column;
    height: 50vh;

    & > ul:first-of-type {
        flex: auto;
        overflow-y: auto;
        list-style: none;
        margin: 0;
        padding: 10px 20px;
    }

    & > div:last-of-type {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
        padding: 16px 20px;
        border-top: 1px solid #ebecf0;
    }

    h3 {
        margin: 0;
        padding: 20px;
        border-bottom: 1px solid #ebecf0;
        font-size: 1.1rem;
        color: #172b4d;
    }
`;

const CssSelected = css`
    background: #deebff;
    color: #0747a6;
    font-weight: 600;
`;

const StyledItem = styled.div<{ $selected?: boolean }>`
    border-radius: 6px;
    cursor: pointer;
    padding: 8px 12px;
    transition: background 0.15s;

    &:hover {
        background: #f4f5f7;
    }

    ${ ({ $selected }) => $selected && CssSelected }
`;

export default function ParameterGroup(props) {
    const { id } = props;

    const readParameter = useQuery(
        gql(ReadParameter),
        {
            variables: {
                id,
            },
        },
    );

    const listParameterGroups = useQuery(gql(ListParameterGroups));

    const [upsertParameter] = useMutation(
        gql(UpsertParameter),
        {
            variables: {
                id,
            },
        },
    );

    const [active, setActive] = useState(false);
    const [selected, setSelected] = useState(undefined);

    useEffect(
        () => {
            setSelected(readParameter.data?.parameter?.group?.id);
        },
        [readParameter.data?.parameter?.group?.id],
    );

    if (readParameter.loading || listParameterGroups.loading) return null;
    if (readParameter.error || listParameterGroups.error) return null;

    async function assign() {
        if (selected === readParameter.data?.parameter.group?.id) return;
        await upsertParameter(
            {
                variables: {
                    definition: {
                        ...readParameter.data.parameter.sourceDefinition,
                        group: { id: selected },
                    },
                },
            }
        );
        setActive(false);
    }

    async function unassign() {
        await upsertParameter(
            {
                variables: {
                    definition: {
                        ...readParameter.data.parameter.sourceDefinition,
                        group: null,
                    },
                },
            }
        );
        setActive(false);
    }

    const pgName = readParameter.data?.parameter?.group?.name[0]?.text ?? "Unassigned";

    return (
        <StyledParameterGroup>
            <span className="label">Parameter Group</span>
            <UI.Button
                onClick={ () => setActive(true) }
                style={ { fontSize: "0.95rem", justifyContent: "flex-start", padding: "10px 14px" } }
            >
                Group: { pgName }
            </UI.Button>

            <Modal
                isOpen={ active }
                onClose={ () => setActive(false) }
            >
                <StyledModalContent>
                    <h3>Assign Parameter Group</h3>
                    <ul>
                        {
                            listParameterGroups.data?.parameterGroups?.map(
                                (parameterGroup) => (
                                    <li key={ parameterGroup.id }>
                                        <StyledItem
                                            $selected={ selected === parameterGroup.id }
                                            onClick={ () => setSelected(parameterGroup.id) }
                                        >
                                            { parameterGroup.name[0]?.text ?? parameterGroup.id }
                                        </StyledItem>
                                    </li>
                                )
                            )
                        }
                    </ul>
                    <div>
                        <UI.Button $appearance="subtle" onClick={ unassign }>Unassign</UI.Button>
                        <UI.Button $appearance="primary" onClick={ assign }>Assign</UI.Button>
                        <UI.Button onClick={ () => setActive(false) }>Cancel</UI.Button>
                    </div>
                </StyledModalContent>
            </Modal>
        </StyledParameterGroup>
    );
}
